/**
 * parseEdgeError · normaliza errores de edge functions al mismo shape.
 *
 * Formato NUEVO (post migración 0135, edge functions con _shared/errors.ts):
 *   { error: 'OCR_ANTHROPIC_5XX', short_id: 'err_a3f21b',
 *     user_message: 'Estamos saturados. Reintentá.', retry: true }
 *
 * Formato VIEJO (edge functions que todavía no se migraron):
 *   { error: 'paso=claude_ocr', detail: '...', hint: '...' }
 *
 * Cuando `err` viene del catch de `supabase.functions.invoke()`, la Response
 * está en `err.context`. Este helper la parsea si puede y devuelve un shape
 * consistente para consumir en la UI (mensaje amigable + shortId para citar
 * en soporte + flag de retry).
 */

export type EdgeErrorParsed = {
  message: string
  shortId?: string
  code?: string
  retry: boolean
}

export async function parseEdgeError(err: unknown): Promise<EdgeErrorParsed> {
  const fallbackMsg =
    (err as { message?: string })?.message ?? 'Error desconocido del servidor.'
  const ctx = (err as { context?: { json?: () => Promise<unknown> } })?.context
  if (!ctx || typeof ctx.json !== 'function') {
    return { message: fallbackMsg, retry: true }
  }
  try {
    const body = (await ctx.json()) as {
      error?: string
      short_id?: string
      user_message?: string
      retry?: boolean
      detail?: string
      hint?: string
    }
    // Formato nuevo
    if (body.short_id && body.user_message) {
      return {
        message: body.user_message,
        shortId: body.short_id,
        code: body.error,
        retry: body.retry ?? true,
      }
    }
    // Formato viejo (endpoints todavía no migrados)
    const legacyMsg = body.detail || body.hint || body.error || fallbackMsg
    return { message: legacyMsg, code: body.error, retry: true }
  } catch {
    return { message: fallbackMsg, retry: true }
  }
}

/**
 * Helper de conveniencia — arma un string listo para mostrar tipo:
 *   "Estamos saturados. Reintentá. (Ref: err_a3f21b)"
 * Sin shortId, solo el mensaje.
 */
export function formatEdgeError(err: EdgeErrorParsed): string {
  if (err.shortId) return `${err.message} (Ref: ${err.shortId})`
  return err.message
}
